// Two-layer CLIENT provably-fair verifier (game presentation, NOT the SDK — the SDK relays `Fairness`
// verbatim, 09-round-contract §7). Recomputes from the revealed seeds:
//   1) RGS/RT-4: SHA-256(serverSeed) == commitment ; randoms = DeriveRandoms(...)
//   2) game: k = IntN(10000) → engine replay → same roll/result/payout.
// checkSettledRound runs BEFORE a settled round is shown (SC-61813): a round whose reveal is missing or malformed, or whose
// seed does not hash to its commitment, is never animated — the roll the game shows is always the reveal's own.
//
// The AMOUNT is the server's, and the game cannot verify it from what it receives. The RGS credits
// clamp(bonus(engine credits) × denomination) (rgs-core-runtime computeSettlement): an operator's maximum-win ceiling
// reduces a win (down to 0: a ceiling of 0 is a supported setting), a bonus adds credits (even to a losing roll), and a
// denomination other than 1 changes the rounding. None of it is on the wire. So NEITHER a credited amount that differs
// from the roll's own payout NOR its sign proves a contradiction, and neither stops the game: the amount is credited
// verbatim, and the door reports whether it equals what the roll pays at denomination 1 (payoutOk) without claiming
// more. Exact settlement verification needs the server to expose the breakdown (SC-62169).

import { commitment as pfCommitment, deriveRandoms, fromHex, intN } from "@blocklabsdev/rgs-game-manager-frontend-api";
// ENGINE-resolve comes from CONTRACTS (@blocklabsdev/rgs-contracts diceResolve), not a local engine mirror. The
// RGS derivation (commitment/deriveRandoms/intN above) STAYS in the SDK — that split is the point of Goal A.
import { resolve as engineResolve, RANDOMS_PER_STEP, RESOLUTION, type Sel } from "../contract";
import type { Fairness } from "@blocklabsdev/rgs-game-manager-frontend-api";

export interface VerifyInput {
  fairness: Fairness;
  sel: Sel;
  betMinor: number;
  /** What the game SHOWED and CREDITED for the round: the animated roll k and the RGS's payout. */
  expected: { k: number; result: "win" | "lose"; payoutMinor: number };
}

export interface VerifyReport {
  /** SHA-256(revealed server seed) is the commitment. */
  commitmentOk: boolean;
  /** The roll recomputed from the reveal is the roll the game showed. */
  rollOk: boolean;
  /** The credited amount is EXACTLY what the roll pays by the game's own rules at denomination 1. False does not mean a
   *  wrong payment: the server may have applied a limit, a bonus or another denomination (see the note above). */
  payoutOk: boolean;
  /** commitmentOk && rollOk — what the game can verify. */
  ok: boolean;
  recomputed: {
    k: number;
    roll: number;
    result: "win" | "lose";
    payoutMinor: number;
    multiplier: number;
  };
}

interface Recomputed {
  commitmentOk: boolean;
  k: number;
  result: "win" | "lose";
  payoutMinor: number;
  multiplierPpm: number;
}

/** The round recomputed from its reveal alone. Throws on a malformed reveal (non-hex seed) or a draw that exhausts. */
async function recompute(f: Fairness, sel: Sel, betMinor: number): Promise<Recomputed> {
  const serverSeed = fromHex(f.serverSeed);
  // Layer 1 — RGS: the revealed server seed hashes to the published commitment.
  const commitmentOk = (await pfCommitment(serverSeed)) === String(f.commitment).toLowerCase();
  // Layer 2 — game: recompute roll and payout from the seeds (same number of randoms the RGS derived).
  const randoms = await deriveRandoms({ serverSeed, publicSeed: f.clientSeed, nonce: f.revealedNonce }, RANDOMS_PER_STEP);
  const { value: k } = intN(randoms, RESOLUTION);
  const r = engineResolve(k, BigInt(betMinor), sel);
  return { commitmentOk, k, result: r.win ? "win" : "lose", payoutMinor: Number(r.winCredits), multiplierPpm: r.multiplierPpm };
}

export async function verifyRound(v: VerifyInput): Promise<VerifyReport> {
  const c = await recompute(v.fairness, v.sel, v.betMinor);
  const rollOk = c.k === v.expected.k && c.result === v.expected.result;
  const payoutOk = c.payoutMinor === v.expected.payoutMinor;
  return {
    commitmentOk: c.commitmentOk,
    rollOk,
    payoutOk,
    ok: c.commitmentOk && rollOk,
    // roll = k / 100 is PRESENTATION (frontend-owned), not engine money-math — computed here, not in contracts.
    recomputed: { k: c.k, roll: c.k / 100, result: c.result, payoutMinor: c.payoutMinor, multiplier: c.multiplierPpm / 1_000_000 },
  };
}

export type SettledCheck =
  | { readonly ok: true; readonly k: number; readonly result: "win" | "lose" }
  | { readonly ok: false; readonly reason: "missing_reveal" | "malformed_reveal" | "commitment_mismatch" | "invalid_amount" };

/** Before a settled round is shown: its roll comes from its reveal, the seed hashes to the commitment, and the credited
 *  amount is a usable money value. The amount itself is the server's (see the note at the top of this file). Never throws. */
export async function checkSettledRound(
  f: Fairness | undefined,
  sel: Sel,
  betMinor: number,
  creditedPayoutMinor: number,
): Promise<SettledCheck> {
  if (!Number.isSafeInteger(creditedPayoutMinor) || creditedPayoutMinor < 0) return { ok: false, reason: "invalid_amount" };
  if (!f || !f.serverSeed || !f.clientSeed || !f.commitment || f.revealedNonce === undefined || f.revealedNonce === null) {
    return { ok: false, reason: "missing_reveal" };
  }
  // the nonce is a uint64 draw counter: a negative or non-integer one is not a reveal (the KDF would silently wrap -1 to
  // 2^64-1 and derive SOME roll from it)
  if (!Number.isSafeInteger(f.revealedNonce) || f.revealedNonce < 0) return { ok: false, reason: "malformed_reveal" };
  let c: Recomputed;
  try {
    c = await recompute(f, sel, betMinor);
  } catch {
    return { ok: false, reason: "malformed_reveal" };
  }
  if (!c.commitmentOk) return { ok: false, reason: "commitment_mismatch" };
  return { ok: true, k: c.k, result: c.result };
}
